import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  RiDashboardLine, RiBookOpenLine, RiQuillPenLine, RiFileTextLine,
  RiUser3Line, RiBarChartLine, RiEditLine, RiNotification3Line, RiMailSendLine,
  RiLogoutBoxLine, RiShieldLine, RiCharacterRecognitionLine, RiToolsLine,
} from 'react-icons/ri';
import { LuPanelLeftOpen, LuPanelRightOpen } from 'react-icons/lu';
import { C } from '../../data/constants';
import { RiCloseLine as RiCloseIcon, RiFeedbackLine } from 'react-icons/ri';
import KitsuMark from './../brand/KitsuMark';

const NAV = [
  { to:'/admin/overview',       label:'Overview',         icon:RiDashboardLine,    section:'main'    },
  { to:'/admin/vocabulary',     label:'Vocabulary',       icon:RiBookOpenLine,     section:'content' },
  { to:'/admin/kana',           label:'Kana (Hira/Kata)', icon:RiCharacterRecognitionLine, section:'content' },
  { to:'/admin/kanji',          label:'Kanji',            icon:RiQuillPenLine,     section:'content' },
  { to:'/admin/grammar',        label:'Grammar',          icon:RiEditLine,         section:'content' },
  { to:'/admin/quiz',           label:'Quiz Questions',   icon:RiFileTextLine,     section:'content' },
  { to:'/admin/practice-tests', label:'Practice Tests',   icon:RiFileTextLine,     section:'content' },
  { to:'/admin/users',          label:'Users',            icon:RiUser3Line,        section:'manage'  },
  { to:'/admin/analytics',      label:'Analytics',        icon:RiBarChartLine,     section:'manage'  },
  { to:'/admin/announcements',  label:'Announcements',    icon:RiNotification3Line,section:'manage'  },
  { to:'/admin/campaign',       label:'Email Campaign',   icon:RiMailSendLine,     section:'manage'  },
  { to:'/admin/feedback',       label:'Feedback',         icon:RiFeedbackLine,     section:'manage'  },
  { to:'/admin/settings',       label:'Site Settings',    icon:RiToolsLine,        section:'manage'  },
];

const SECTIONS = [
  { key:'main',    label:null       },
  { key:'content', label:'Content'  },
  { key:'manage',  label:'Manage'   },
];

const MOBILE_BREAKPOINT = 1024;

export default function AdminSidebar({ onLogout, isOpen, onClose, collapsed, onToggleCollapse }) {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);

  // Collapse is desktop-only. On small screens the sidebar is a drawer opened
  // from the mobile header, and it must always render expanded.
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= MOBILE_BREAKPOINT;
  });

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const effCollapsed = isMobile ? false : collapsed && !isHovered;
  const isHoverPreview = !isMobile && collapsed && isHovered;

  return (
    <aside
      className={`app-sidebar ${isOpen ? 'open' : ''} ${effCollapsed ? 'collapsed' : ''} ${isHoverPreview ? 'hover-preview' : ''}`}
      onMouseEnter={() => { if (!isMobile && collapsed) setIsHovered(true); }}
      onMouseLeave={() => setIsHovered(false)}
      style={{ width:effCollapsed ? 66 : 224, flexShrink:0, background:C.surface, borderRight:`1px solid ${C.border}`, display:'flex', flexDirection:'column', padding:effCollapsed ? '18px 6px' : '18px 12px', gap:2, position:'fixed', left:0, top:0, height:'100vh', overflowY:'auto', overflowX:'hidden', zIndex: isMobile ? undefined : (isHoverPreview ? 60 : 1), willChange:'width, padding, transform', transition:'width 0.18s cubic-bezier(0.22, 1, 0.36, 1), padding 0.18s cubic-bezier(0.22, 1, 0.36, 1), transform 0.18s cubic-bezier(0.22, 1, 0.36, 1)' }}
    >
      <button className="sidebar-close-btn" onClick={onClose} aria-label="Close Menu">
        <RiCloseIcon size={20} />
      </button>
      <div style={{ minHeight:'60px', display:'flex', alignItems:'center', justifyContent:effCollapsed ? 'center' : 'flex-start', gap:8, padding:'4px 6px 20px', whiteSpace:'nowrap' }}>
        <KitsuMark size={24} style={{ flexShrink:0 }} />
        {!effCollapsed && (
          <div>
            <div style={{ fontWeight:900, fontSize:15, letterSpacing:-0.4, lineHeight:1.2 }}>
              Kitsu<span style={{ color:C.accent }}>Speak</span>
            </div>
            <div style={{ fontSize:10, color:C.accent, fontWeight:700, letterSpacing:1, display:'flex', alignItems:'center', gap:3 }}>
              <RiShieldLine size={10} /> ADMIN PANEL
            </div>
          </div>
        )}
      </div>

      {SECTIONS.map(sec => {
        const items = NAV.filter(n => n.section === sec.key);
        return (
          <div key={sec.key}>
            {sec.label && (
              <div style={{ minHeight: 25, opacity: effCollapsed ? 0 : 1, fontSize:10, fontWeight:700, color:C.faint, padding:'10px 6px 5px', letterSpacing:1, textTransform:'uppercase', transition:'opacity 0.18s ease' }}>
                {sec.label}
              </div>
            )}
            {items.map(({ to, label, icon:Icon }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
                onClick={onClose}
                title={effCollapsed ? label : undefined}
                style={effCollapsed ? { justifyContent:'center', padding:'9px 7px', minHeight:40 } : { minHeight:40 }}
              >
                <Icon size={effCollapsed ? 19 : 15} />
                {!effCollapsed && label}
              </NavLink>
            ))}
          </div>
        );
      })}

      <div style={{ flex:1 }} />

      <div style={{ borderTop:`1px solid ${C.border}`, paddingTop:12, display:'flex', flexDirection:'column', gap:2 }}>
        {!isMobile && (
          <button
            className="nav-item"
            type="button"
            onClick={onToggleCollapse}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            style={{ justifyContent:effCollapsed ? 'center' : 'flex-start', width:'100%', background:'transparent', minHeight:40, padding: effCollapsed ? '9px 7px' : '9px 12px' }}
          >
            {collapsed ? <LuPanelLeftOpen size={effCollapsed ? 19 : 16} /> : <LuPanelRightOpen size={16} />}
            {!effCollapsed && (collapsed ? 'Expand' : 'Collapse')}
          </button>
        )}
        <div className="nav-item" title={effCollapsed ? 'Logout' : undefined} style={{ color:C.red, justifyContent:effCollapsed ? 'center' : 'flex-start', minHeight:40, padding: effCollapsed ? '9px 7px' : '9px 12px' }} onClick={() => { onLogout(); navigate('/admin'); }}>
          <RiLogoutBoxLine size={effCollapsed ? 19 : 15} />
          {!effCollapsed && 'Logout'}
        </div>
      </div>
    </aside>
  );
}
